import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type MutationFunctionContext,
  type RequestQueryObject,
} from '@frontend/api';

import type { DxfSceneMapping, DxfSceneMappingWriteVariables } from './types';
import { getDxfSceneMappingQueryKey } from './useGetDxfSceneMapping';

const useUpdateDxfSceneMappingKey = ['dxf', 'sceneMapping', 'update'] as const;
export const getUseUpdateDxfSceneMappingKey = () => useUpdateDxfSceneMappingKey;

/**
 * Mutation function for updating a DXF scene mapping.
 */
export const useUpdateDxfSceneMapping: MutationFunction<
  DxfSceneMapping,
  DxfSceneMappingWriteVariables,
  [],
  RequestQueryObject
> = ({ requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();

  return useMutation<
    DxfSceneMapping,
    unknown,
    DxfSceneMappingWriteVariables,
    MutationFunctionContext<DxfSceneMapping>
  >({
    mutationFn: ({ partFileUuid, ...body }) =>
      request<DxfSceneMapping>(`/dxf/${partFileUuid}/mapping`, {
        init: {
          method: 'PATCH',
          body,
        },
      }),
    ...queryOptions,
    onSuccess: async (...args) => {
      queryOptions?.onSuccess?.(...args);
      const [mapping, variables] = args;
      const mappingKey = getDxfSceneMappingQueryKey(variables.partFileUuid);
      await queryClient.cancelQueries({ queryKey: mappingKey });
      queryClient.setQueryData<DxfSceneMapping | null>(mappingKey, mapping);
    },
    onSettled: (...args) => {
      queryOptions?.onSettled?.(...args);
      const variables = args[2];
      queryClient.invalidateQueries({
        queryKey: getDxfSceneMappingQueryKey(variables.partFileUuid),
      });
    },
    mutationKey: getUseUpdateDxfSceneMappingKey(),
  });
};
